import type { TicketDto } from '@planuze/admin-contracts';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@planuze/ui';
import { useSupportContent } from '../../content/sections/support.js';
import { SegmentedControl, type SegmentOption } from '../filters/segmented-control.js';
import { SearchInput } from '../search-input.js';

export type StatusFilter = TicketDto['status'] | 'all';
export type PriorityFilter = TicketDto['priority'] | 'all';
/* Janela de tempo (mapeada p/ `createdFrom` server-side), origem (org vs individual) e
   atribuição (meus/não-atribuídos) — todas server-side no `listTicketsQuery` (ADR-0352). */
export type PeriodFilter = 'all' | '24h' | '7d' | '30d';
export type SegmentFilter = 'all' | 'org' | 'individual';
export type AssignmentFilter = 'all' | 'mine' | 'unassigned';

export interface InboxFiltersValue {
  readonly search: string;
  readonly status: StatusFilter;
  readonly priority: PriorityFilter;
  readonly assignment: AssignmentFilter;
  readonly period: PeriodFilter;
  readonly segment: SegmentFilter;
}

interface Props {
  readonly value: InboxFiltersValue;
  readonly onChange: (value: InboxFiltersValue) => void;
}

/* Toolbar da inbox de suporte (ADR-0352): SearchInput por assunto + status/prioridade
   (Select) + atribuição/período/segmento (SegmentedControl). Todas as facetas alimentam a
   query server-side — nada é filtrado sobre as páginas já carregadas.
   Espaçamento fica a cargo do PageContent (space-y) — sem margens locais. */
export function InboxFilters({ value, onChange }: Props) {
  const copy = useSupportContent();

  const periodOptions: readonly SegmentOption<PeriodFilter>[] = [
    { value: 'all', label: copy.inboxFilters.periodOptions.all },
    { value: '24h', label: copy.inboxFilters.periodOptions['24h'] },
    { value: '7d', label: copy.inboxFilters.periodOptions['7d'] },
    { value: '30d', label: copy.inboxFilters.periodOptions['30d'] },
  ];
  const segmentOptions: readonly SegmentOption<SegmentFilter>[] = [
    { value: 'all', label: copy.inboxFilters.segmentOptions.all },
    { value: 'org', label: copy.inboxFilters.segmentOptions.org },
    { value: 'individual', label: copy.inboxFilters.segmentOptions.individual },
  ];
  const assignmentOptions: readonly SegmentOption<AssignmentFilter>[] = [
    { value: 'all', label: copy.inboxFilters.assignmentOptions.all },
    { value: 'mine', label: copy.inboxFilters.assignmentOptions.mine },
    { value: 'unassigned', label: copy.inboxFilters.assignmentOptions.unassigned },
  ];

  return (
    <div className="flex flex-wrap items-center gap-3">
      <SearchInput
        value={value.search}
        onChange={(search) => onChange({ ...value, search })}
        ariaLabel={copy.inboxFilters.searchAriaLabel}
        placeholder={copy.inboxFilters.searchPlaceholder}
      />
      <Select
        value={value.status}
        onValueChange={(status) => onChange({ ...value, status: status as StatusFilter })}
      >
        <SelectTrigger className="w-40" aria-label={copy.inboxFilters.statusAriaLabel}>
          <SelectValue placeholder={copy.inboxFilters.statusPlaceholder} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">{copy.inboxFilters.statusOptions.all}</SelectItem>
          <SelectItem value="open">{copy.inboxFilters.statusOptions.open}</SelectItem>
          <SelectItem value="in_progress">{copy.inboxFilters.statusOptions.inProgress}</SelectItem>
          <SelectItem value="closed">{copy.inboxFilters.statusOptions.closed}</SelectItem>
        </SelectContent>
      </Select>
      <Select
        value={value.priority}
        onValueChange={(p) => onChange({ ...value, priority: p as PriorityFilter })}
      >
        <SelectTrigger className="w-40" aria-label={copy.inboxFilters.priorityAriaLabel}>
          <SelectValue placeholder={copy.inboxFilters.priorityPlaceholder} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">{copy.inboxFilters.priorityOptions.all}</SelectItem>
          <SelectItem value="urgent">{copy.inboxFilters.priorityOptions.urgent}</SelectItem>
          <SelectItem value="high">{copy.inboxFilters.priorityOptions.high}</SelectItem>
          <SelectItem value="normal">{copy.inboxFilters.priorityOptions.normal}</SelectItem>
          <SelectItem value="low">{copy.inboxFilters.priorityOptions.low}</SelectItem>
        </SelectContent>
      </Select>
      <SegmentedControl
        value={value.assignment}
        onChange={(assignment) => onChange({ ...value, assignment })}
        options={assignmentOptions}
        ariaLabel={copy.inboxFilters.assignmentAriaLabel}
      />
      <SegmentedControl
        value={value.period}
        onChange={(period) => onChange({ ...value, period })}
        options={periodOptions}
        ariaLabel={copy.inboxFilters.periodAriaLabel}
      />
      <SegmentedControl
        value={value.segment}
        onChange={(segment) => onChange({ ...value, segment })}
        options={segmentOptions}
        ariaLabel={copy.inboxFilters.segmentAriaLabel}
      />
    </div>
  );
}
